import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { MetagameWidgetType } from '../../../types';

import styles from './MetagameWideWidget.module.scss';

interface MetagameWideWidgetProps {
  metagameType: MetagameWidgetType;
  balanceValue: string;
  widgetImageUrl?: string;
  timer?: ReactNode;
}

const getText = (metagameType: MetagameWidgetType, t: TFunction<'translation', undefined>): string => {
  switch (metagameType) {
    case 'puzzle':
      return t('metagameWidget.piecesLeft');
    case 'roulette':
      return t('metagameWidget.chipsLeft');
    case 'trail':
      return t('metagameWidget.diceLeft');
    case 'hammer':
      return t('metagameWidget.hammersLeft');
    case 'plinko':
      return t('metagameWidget.dropsLeft');
    default:
      return '';
  }
};

export function MetagameWideWidget(props: MetagameWideWidgetProps) {
  const { widgetImageUrl, metagameType, balanceValue, timer } = props;
  const { t } = useTranslation();
  const textRef = useRef<HTMLSpanElement>(null);

  const text = getText(metagameType, t);

  useEffect(() => {
    const el = textRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) {
      return;
    }

    const fit = () => {
      el.style.fontSize = '';
      let fontSize = parseFloat(getComputedStyle(el).fontSize);

      while (parent.scrollWidth > parent.clientWidth && fontSize > 4) {
        fontSize -= 0.5;
        el.style.fontSize = `${fontSize}px`;
      }
    };

    fit();

    const observer = new ResizeObserver(fit);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [text]);

  return (
    <div className={styles.container}>
      {widgetImageUrl && <img alt="" className={styles.img} src={widgetImageUrl} />}
      <div className={styles.content}>
        <div className={styles.textRow}>
          <span className={styles.counterText}>{balanceValue}</span>
          <span ref={textRef} className={styles.text}>
            {text}
          </span>
        </div>
        <div className={styles.timer}>{timer}</div>
      </div>
    </div>
  );
}
